import { gql } from '@apollo/client';
import { RCButton } from '@reverbdotcom/cadence/components';
import React from 'react';
import I18n from 'i18n-js';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import Location from '../lib/wrapped_location';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { CheckoutGuestAuthModal } from './CheckoutGuestAuthModal';
import {
  BuyItNowButtonFields,
} from '../schemas/rql';
import { isOwner } from '@reverbdotcom/commons/src/user_context_helpers';
import { BuyItNowPaymentOptions, BuyItNowPaymentOptionsFragment } from './BuyItNowPaymentOptions';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

type Listing = BuyItNowButtonFields.Fragment;

interface Props {
  listing: Listing;
  quantity: number;
}

export function BuyItNowButton({ listing, quantity }: Props) {
  const user = useUser();
  const showGuestAuthModal = user.loggedOut;
  const [isPaymentMethodModalOpen, setPaymentMethodModalOpen] = React.useState(false);
  const [guestRedirectUrl, setGuestRedirectUrl] = React.useState(null);

  function onButtonClick() {
    const customTrackedValues = {
      userId: user.id,
      isLocalPickupOnly: listing.shipping.localPickupOnly,
      isStandalonePaypal: listing.acceptedPaymentMethods.includes('paypal'),
    };

    const trackedExperiments = [];

    if (isEligibleForPaypalPayLaterExp(listing)) {
      trackedExperiments.push(experiments.PAY_LATER_BUY_NOW);
    }

    const formattedUserExperiments = formatExperimentsForEventAttributes(user, trackedExperiments);

    setPaymentMethodModalOpen(true);
    trackEvent({
      eventName: MParticleEventName.ClickedBuyItNowButton,
      listingId: listing.id,
      experiments: formattedUserExperiments,
      ...customTrackedValues,
    });
  }

  function onStartCheckoutSuccess(redirectUrl: string) {
    if (showGuestAuthModal) {
      setPaymentMethodModalOpen(false);
      setGuestRedirectUrl(redirectUrl);
    } else {
      Location.assign(redirectUrl);
    }
  }

  return (
    <div className="buy-it-now-button__wrapper">
      <BuyItNowPaymentOptions
        listing={listing}
        quantity={quantity}
        modalOpen={isPaymentMethodModalOpen}
        onRequestClose={() => setPaymentMethodModalOpen(false)}
        onStartCheckoutSuccess={onStartCheckoutSuccess}
      />
      <RCButton
        variant="primary"
        onClick={onButtonClick}
        fullWidth
        disabled={isOwner(user, listing)}
      >
        {I18n.t('discovery.checkout.buyItNow.title')}
      </RCButton>
      <CheckoutGuestAuthModal
        active={!!guestRedirectUrl}
        redirectTo={guestRedirectUrl}
        trackSource="BuyItNowButton"
        onRequestClose={() => setGuestRedirectUrl(null)}
      />
    </div>
  );
}

function isEligibleForPaypalPayLaterExp(listing: Listing) {
  const acceptedPaymentMethods = listing.acceptedPaymentMethods || [];
  return acceptedPaymentMethods.includes('paypal_pay_later');
}

export const BuyItNowButtonFragment = gql`
  fragment BuyItNowButtonFields on Listing {
    _id
    id
    shipping {
      localPickupOnly
    }
    ...BuyItNowPaymentOptionsFields
  }
  ${BuyItNowPaymentOptionsFragment}
`;
